import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useDocumentRequestsQuery,
  useDocumentsMutation,
  useDocumentsQuery
} from "../../queries/useDocuments"
import { useQueryClient } from "@tanstack/react-query"

type Props = {
  patientId: string
  goNext: () => void
}

type ReportType = "MRI_REPORT" | "SCANNER_REPORT" | "OTHER"

const FileUploader = (props: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [comment, setComment] = useState<string>("")
  const [reportType, setReportType] = useState<ReportType>()

  const reportTypesDisplay = [
    { key: "MRI_REPORT", label: t("documents.mri_report") },
    { key: "SCANNER_REPORT", label: t("documents.scanner_report") },
    { key: "OTHER", label: t("documents.other") }
  ]

  const { data: medicalDocuments, isLoading } = useDocumentsQuery(
    props.patientId
  )
  const { data: medicalDocumentRequests } = useDocumentRequestsQuery(
    props.patientId
  )

  const { mutateAsync, isPending } = useDocumentsMutation(props.patientId)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0])
    }
  }

  const handleUpload = async () => {
    if (file) {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("document_type", reportType ?? "MRI_REPORT")
      formData.append("comment", comment)

      mutateAsync(formData).then(() => {
        queryClient.invalidateQueries({ queryKey: ["medical_documents"] })
        queryClient.invalidateQueries({
          queryKey: ["medical_documents_requests"]
        })
      })
    }
  }

  const onChangeReportType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setReportType(value as ReportType)
  }

  return (
    <div className="flex flex-col items-center justify-center h-full w-full mx-4">
      {!medicalDocuments?.length || medicalDocumentRequests ? (
        <div className="flex flex-col overflow-auto items-center justify-center h-full w-full lg:w-2/3 xl:w-1/2 xl:h-3/4 px-4 lg:px-8 py-2 border gap-4 rounded-xl">
          <h1 className="text-md font-bold text-cortexx-900 text-center">
            {t("documents.question_upload")}
          </h1>
          <h2 className="font-normal text-cortexx-900 text-center">
            {t("documents.facilitate_analysis")}
          </h2>
          <div className="flex flex-col w-full items-center justify-center mb-8">
            <form
              key={"document_upload"}
              className="flex flex-col items-start w-full"
            >
              <div className="mt-2 w-full">
                <label className="text-cortexx-900 text-sm text-start font-bold">
                  {t("documents.document_type_label")}
                </label>
                <div className="flex flex-col mt-2 md:mt-3">
                  {reportTypesDisplay.map((option) => (
                    <div className="flex flex-row mr-8">
                      <input
                        type="checkbox"
                        name={option.label}
                        id={option.key}
                        value={option.key}
                        onChange={onChangeReportType}
                        checked={reportType === option.key}
                      />
                      <label className="ml-2 text-sm">{option.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              <input
                className="justify-center text-sm text-cortexx-550 mt-4 file:mr-5 file:py-3 file:px-6 file:border file:border-cortexx-550 file:rounded-lg file:text-sm file:bg-cortexx-550 file:text-cortexx-50 hover:file:cursor-pointer hover:file:bg-cortexx-50 hover:file:text-cortexx-550 hover:file:border hover:file:border-cortexx-550"
                id="file_input"
                type="file"
                onChange={handleFileChange}
              />
              <div className="mt-4 w-full">
                <label className="text-cortexx-900 text-sm text-start font-bold">
                  {t("documents.comment_label")}
                </label>
                <input
                  type="text"
                  id="comment"
                  className="mt-2 border border-cortexx-550 rounded-md p-2 w-full text-sm"
                  placeholder={t("documents.comment_placeholder")}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </form>
          </div>

          <div className="flex w-full justify-end items-start">
            {!isPending ? (
              <button
                onClick={handleUpload}
                disabled={!file}
                className="bg-cortexx-500 text-white font-semibold py-3 px-5 rounded-lg"
              >
                {t("documents.send_document")}
              </button>
            ) : (
              <button
                className="bg-cortexx-500 text-white font-semibold py-3 px-5 rounded-lg"
                disabled
              >
                <img
                  src="/images/fade-stagger-circles.svg"
                  style={{ height: "20px" }}
                  alt="close"
                  loading="lazy"
                />
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <h1 className="text-lg font-normal text-cortexx-900 text-center mb-4">
            {t("documents.documents_received")}
          </h1>
          <h2 className="font-normal text-cortexx-900 text-center">
            {t("documents.next_step")}
          </h2>
          <div className="mt-4">
            <button
              onClick={() => props.goNext()}
              className="bg-cortexx-500 text-white font-semibold py-3 px-5 rounded-lg"
            >
              {t("documents.skip")}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default FileUploader
